import {
  useCreateShareMutation,
  useGetCurrentUserQuery,
  useGetDemoQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import {
  type IPresentation,
  type IntegrationVendor,
  type ShareLink,
  ShareSourceType,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  removeUndefinedValues,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo, useState } from 'react';
import { CopyShareLinkModal } from '../../../Share';
import {
  ShareDemoModal,
  type ShareDemoModalState,
  getInitialShareDemoModalValues,
} from '../../../Share/ShareDemoModal';
import { useDemoPageProps } from '../DemoPageContext';

export const SharePresentationFlow = ({
  onClose,
  id,
  variables,
  onViewShares,
}: {
  onClose: () => void;
  id: number;
  variables: IPresentation['variables'] | null;
  onViewShares?: () => void;
}) => {
  const flags = useFlags();
  const { trackEvent } = useAnalytics();
  const { demoId, organizationId, workspaceId } = useDemoPageProps();
  const { data: organization } = useGetOrganizationQuery(
    {
      id: organizationId,
    },
    { skip: !organizationId }
  );
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const { data: userResponse } = useGetCurrentUserQuery();
  const { user } = userResponse || {};

  const initialValues = useMemo(
    () =>
      demo &&
      user &&
      getInitialShareDemoModalValues(
        demo.demoName,
        user,
        variables ?? [],
        flags.welcomeMatTitle
          ? undefined
          : `${organization.organizationName} product demo`
      ),
    [demo, variables, user, flags?.welcomeMatTitle]
  );
  const [showShareDemoModal, { setFalse: setFalseShowShareDemoModal }] =
    useBoolean(true);
  const [
    showCopyShareLinkModal,
    {
      setTrue: setTrueCopyShareLinkModal,
      setFalse: setFalseCopyShareLinkModal,
    },
  ] = useBoolean(false);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [createShareLink, { isLoading: isLoadingCreateShare }] =
    useCreateShareMutation();
  const handleCreateShareLink = ({
    linkName,
    skipWelcome,
    cover,
    expiresAt,
    daysToExpire,
    security,
    salesforceLeads,
    hubspotLeads,
    slackIntegration,
    showGuidanceTooltip,
    welcomeMatTitle,
  }: ShareDemoModalState) => {
    createShareLink({
      organizationId,
      workspaceId,
      sourceType: ShareSourceType.presentation,
      id,
      linkDescription: linkName,
      skipWelcome,
      cover: flags?.welcomeMatConfig ? cover : undefined,
      expiresAt,
      daysToExpire,
      security,
      integrationLeads: Object.keys(
        removeUndefinedValues({
          salesforce: salesforceLeads,
          hubspot: hubspotLeads,
          slack: slackIntegration,
        })
      ) as IntegrationVendor[],
      showGuidanceTooltip,
      welcomeMatTitle,
    })
      .unwrap()
      .then((share) => {
        notify.success('Link was created successfully');
        setShareLink(share);
        setFalseShowShareDemoModal();
        setTrueCopyShareLinkModal();
      })
      .catch(() => {
        onClose();
        notify.error('Sorry, something went wrong');
        setFalseShowShareDemoModal();
      });
  };
  const location = 'demo_info_page_create_share_presentation_modal';
  return (
    <>
      {demo && initialValues && (
        <ShareDemoModal
          demoId={demoId}
          title="Share presentation"
          initialValues={initialValues}
          handleSubmit={(values) => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_SHARE_PRESENTATION_APPROVE,
              {
                location,
                shareData: values,
              }
            );
            handleCreateShareLink(values);
          }}
          open={showShareDemoModal}
          onClose={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_SHARE_PRESENTATION_APPROVE,
              {
                location,
              }
            );
            onClose();
            setFalseShowShareDemoModal();
          }}
          isLoading={isLoadingCreateShare}
        />
      )}
      {shareLink && (
        <CopyShareLinkModal
          goToShares={() => onViewShares?.()}
          open={showCopyShareLinkModal}
          onClose={() => {
            onClose();
            setFalseCopyShareLinkModal();
          }}
          shareLink={shareLink}
          containerClass="w-[680px]"
        />
      )}
    </>
  );
};
